import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { FaFilter, FaTimes, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const FilterSection = ({ title, children, isOpen, onToggle }) => (
    <div className="border rounded-lg p-4 bg-gray-50 mb-4">
        <div
            className="flex justify-between items-center cursor-pointer"
            onClick={onToggle}
        >
            <h3 className="text-lg font-medium text-gray-700">{title}</h3>
            <button type="button" className="text-gray-500 hover:text-gray-700">
                {isOpen ? <FaChevronUp /> : <FaChevronDown />}
            </button>
        </div>
        {isOpen && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                {children}
            </div>
        )}
    </div>
);

const InputField = ({ label, register, name, type = "text", placeholder, min, max, step }) => (
    <div className="relative">
        <label className="block text-sm font-medium mb-1 text-gray-700">{label}</label>
        <input
            type={type}
            {...register(name)}
            className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
            placeholder={placeholder}
            min={min}
            max={max}
            step={step}
        />
    </div>
);

const SelectField = ({ label, register, name, options }) => (
    <div className="relative">
        <label className="block text-sm font-medium mb-1 text-gray-700">{label}</label>
        <select
            {...register(name)}
            className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-white"
        >
            <option value="">All</option>
            {options.map(option => (
                <option key={option.value} value={option.value}>
                    {option.label}
                </option>
            ))}
        </select>
    </div>
);

const ReceiptFilters = ({ onApplyFilters, onResetFilters }) => {
    const { register, handleSubmit, reset, watch } = useForm();
    const [openSections, setOpenSections] = useState({
        basic: true,
        location: false,
        status: false,
        date: false,
        consignor: false,
        consignee: false,
        driver: false,
        amount: false,
        vehicle: false
    });

    const toggleSection = (section) => {
        setOpenSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const watchedFields = watch();
    const hasActiveFilters = Object.values(watchedFields).some(value => value !== "" && value !== undefined);

    const onSubmit = (data) => {
        const filters = Object.entries(data).reduce((acc, [key, value]) => {
            if (value !== '' && value !== undefined) {
                if (['minWeight', 'maxWeight', 'minRate', 'maxRate', 'minAmount', 'maxAmount'].includes(key)) {
                    acc[key] = parseFloat(value);
                } else {
                    acc[key] = value;
                }
            }
            return acc;
        }, {});
        onApplyFilters(filters);
    };

    const handleReset = () => {
        reset();
        onResetFilters();
    };

    return (
        <div className="bg-white p-6 rounded-xl shadow-lg mb-6">
            <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                    <FaFilter className="mr-2" />
                    Filter Receipts
                    {hasActiveFilters && (
                        <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                            Filters Active
                        </span>
                    )}
                </h2>
                <div className="flex space-x-2">
                    <button
                        type="button"
                        onClick={handleReset}
                        className="flex items-center px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                    >
                        <FaTimes className="mr-2" />
                        Reset All
                    </button>
                </div>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <FilterSection
                    title="Basic Information"
                    isOpen={openSections.basic}
                    onToggle={() => toggleSection('basic')}
                >
                    <InputField
                        label="Receipt Number"
                        register={register}
                        name="receiptNumber"
                        placeholder="Search receipt number"
                    />
                    <InputField
                        label="Goods Description"
                        register={register}
                        name="goodsDescription"
                        placeholder="Search goods description"
                    />
                </FilterSection>

                <FilterSection
                    title="Location Details"
                    isOpen={openSections.location}
                    onToggle={() => toggleSection('location')}
                >
                    <InputField
                        label="From Location"
                        register={register}
                        name="fromLocation"
                        placeholder="From location"
                    />
                    <InputField
                        label="To Location"
                        register={register}
                        name="toLocation"
                        placeholder="To location"
                    />
                </FilterSection>

                <FilterSection
                    title="Status Information"
                    isOpen={openSections.status}
                    onToggle={() => toggleSection('status')}
                >
                    <SelectField
                        label="Receipt Status"
                        register={register}
                        name="status"
                        options={[
                            { value: 'ACTIVE', label: 'Active' },
                            { value: 'DELIVERED', label: 'Delivered' },
                            { value: 'CANCELLED', label: 'Cancelled' }
                        ]}
                    />
                    <InputField
                        label="Advance Payment"
                        register={register}
                        name="advancePayment"
                        placeholder="Advance Payment"
                    />
                    <SelectField
                        label="Payment Status"
                        register={register}
                        name="paymentStatus"
                        options={[
                            { value: 'PAID', label: 'Paid' },
                            { value: 'PENDING', label: 'Pending' },
                            { value: 'PARTIAL', label: 'Partial' }
                        ]}
                    />
                </FilterSection>

                <FilterSection
                    title="Date Range"
                    isOpen={openSections.date}
                    onToggle={() => toggleSection('date')}
                >
                    <InputField
                        label="From Date"
                        register={register}
                        name="fromDate"
                        type="date"
                    />
                    <InputField
                        label="To Date"
                        register={register}
                        name="toDate"
                        type="date"
                    />
                </FilterSection>

                <FilterSection
                    title="Consignor Details"
                    isOpen={openSections.consignor}
                    onToggle={() => toggleSection('consignor')}
                >
                    <InputField
                        label="Consignor Name"
                        register={register}
                        name="consignorName"
                        placeholder="Search consignor name"
                    />
                    <InputField
                        label="Consignor GSTIN"
                        register={register}
                        name="consignorGstin"
                        placeholder="Search consignor GSTIN"
                    />
                    <InputField
                        label="Consignor Contact"
                        register={register}
                        name="consignorContact"
                        placeholder="Search consignor contact"
                    />
                </FilterSection>

                <FilterSection
                    title="Consignee Details"
                    isOpen={openSections.consignee}
                    onToggle={() => toggleSection('consignee')}
                >
                    <InputField
                        label="Consignee Name"
                        register={register}
                        name="consigneeName"
                        placeholder="Search consignee name"
                    />
                    <InputField
                        label="Consignee GSTIN"
                        register={register}
                        name="consigneeGstin"
                        placeholder="Search consignee GSTIN"
                    />
                    <InputField
                        label="Consignee Contact"
                        register={register}
                        name="consigneeContact"
                        placeholder="Search consignee contact"
                    />
                </FilterSection>

                <FilterSection
                    title="Driver Details"
                    isOpen={openSections.driver}
                    onToggle={() => toggleSection('driver')}
                >
                    <InputField
                        label="Driver Name"
                        register={register}
                        name="driverName"
                        placeholder="Search driver name"
                    />
                    <InputField
                        label="Driver Contact"
                        register={register}
                        name="driverContact"
                        placeholder="Search driver contact"
                    />
                    <InputField
                        label="Owner Name"
                        register={register}
                        name="ownerName"
                        placeholder="Search owner name"
                    />
                    <InputField
                        label="Owner Contact"
                        register={register}
                        name="ownerNumber"
                        placeholder="Search owner contact"
                    />
                </FilterSection>

                <FilterSection
                    title="Vehicle Details"
                    isOpen={openSections.vehicle}
                    onToggle={() => toggleSection('vehicle')}
                >
                    <InputField
                        label="Vehicle Number"
                        register={register}
                        name="vehicleNumber"
                        placeholder="Search vehicle number"
                    />
                    <div>
                        <label className="block text-sm font-medium mb-1 text-gray-700">
                            Chassis Number
                            <span className="ml-1 text-gray-400 cursor-help group relative">
                                ⓘ
                                <span className="hidden group-hover:block absolute z-10 w-48 p-2 mt-1 text-xs text-gray-600 bg-white border rounded shadow-lg">
                                    17-character vehicle identification number
                                </span>
                            </span>
                        </label>
                        <input
                            type="text"
                            {...register("chassisNumber")}
                            className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 uppercase"
                            placeholder="Enter chassis number"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1 text-gray-700">
                            Engine Number
                            <span className="ml-1 text-gray-400 cursor-help group relative">
                                ⓘ
                                <span className="hidden group-hover:block absolute z-10 w-48 p-2 mt-1 text-xs text-gray-600 bg-white border rounded shadow-lg">
                                    6-14 character engine identification number
                                </span>
                            </span>
                        </label>
                        <input
                            type="text"
                            {...register("engineNumber")}
                            className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 uppercase"
                            placeholder="Enter engine number"
                        />
                    </div>
                </FilterSection>

                <FilterSection
                    title="Amount & Weight Details"
                    isOpen={openSections.amount}
                    onToggle={() => toggleSection('amount')}
                >
                    <InputField
                        label="Min Weight (kg)"
                        register={register}
                        name="minWeight"
                        type="number"
                        placeholder="Minimum weight"
                        min="0"
                        step="0.01"
                    />
                    <InputField
                        label="Max Weight (kg)"
                        register={register}
                        name="maxWeight"
                        type="number"
                        placeholder="Maximum weight"
                        min="0"
                        step="0.01"
                    />
                    <InputField
                        label="Min Rate (₹)"
                        register={register}
                        name="minRate"
                        type="number"
                        placeholder="Minimum rate"
                        min="0"
                        step="0.01"
                    />
                    <InputField
                        label="Max Rate (₹)"
                        register={register}
                        name="maxRate"
                        type="number"
                        placeholder="Maximum rate"
                        min="0"
                        step="0.01"
                    />
                    <InputField
                        label="Min Amount (₹)"
                        register={register}
                        name="minAmount"
                        type="number"
                        placeholder="Minimum amount"
                        min="0"
                        step="0.01"
                    />
                    <InputField
                        label="Max Amount (₹)"
                        register={register}
                        name="maxAmount"
                        type="number"
                        placeholder="Maximum amount"
                        min="0"
                        step="0.01"
                    />
                </FilterSection>

                <div className="sticky bottom-0 bg-white p-4 border-t mt-6 flex justify-end space-x-2">
                    <button
                        type="submit"
                        className="flex items-center px-6 py-2.5 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors duration-200 shadow-md hover:shadow-lg"
                    >
                        <FaFilter className="mr-2" />
                        Apply Filters
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ReceiptFilters;
